/**
 * Master Muscles — Cross-browser compatibility layer
 * ---------------------------------------------------------------------------
 * MUST be the first theme script to execute. Written in ES5 so it still runs
 * inside old WebViews, where a syntax error would take down the whole file.
 *
 * Responsibilities:
 *   1. Environment detection (engine, browser, in-app WebView) -> html classes
 *   2. Storage that never throws (Safari private mode, blocked in-app storage)
 *   3. Small polyfills for APIs the theme assumes exist
 *   4. CSS feature detection -> html classes consumed by mm-compat.css
 *   5. Hardened cart transport + a single cart-mutation event bus
 *   6. Error capture, so browser-specific failures stop being silent
 *
 * Public API: window.MMCompat
 */
(function (window, document) {
  'use strict';

  if (window.MMCompat && window.MMCompat.version) return;

  var VERSION = '1.0.0';
  var HTML = document.documentElement;
  var UA = (navigator && navigator.userAgent) || '';
  var VENDOR = (navigator && navigator.vendor) || '';

  function addClass(name) {
    if (!name) return;
    if (HTML.classList) HTML.classList.add(name);
    else if ((' ' + HTML.className + ' ').indexOf(' ' + name + ' ') === -1) HTML.className += ' ' + name;
  }

  /* =========================================================================
   * 1. Environment detection
   * =======================================================================*/

  var IN_APP_PATTERNS = [
    ['instagram', /Instagram/i],
    ['facebook', /FBAN|FBAV|FB_IAB|FB4A|FBIOS|MetaIAB/i],
    ['messenger', /Messenger|MessengerLite/i],
    ['threads', /Barcelona|Threads/i],
    ['tiktok', /BytedanceWebview|musical_ly|Bytedance|TikTok|trill_/i],
    ['snapchat', /Snapchat/i],
    ['pinterest', /Pinterest/i],
    ['twitter', /Twitter|TwitterAndroid/i],
    ['linkedin', /LinkedInApp|LinkedIn/i],
    ['whatsapp', /WhatsApp/i],
    ['telegram', /Telegram/i],
    ['line', /\bLine\//i],
    ['wechat', /MicroMessenger/i],
    ['kakao', /KAKAOTALK/i],
    ['viber', /Viber/i],
    ['gsa', /GSA\//i],
    ['amazon', /Silk\//i],
    ['oem', /HuaweiBrowser|HeyTap|OppoBrowser|VivoBrowser|MiuiBrowser/i]
  ];

  function detectInApp() {
    for (var i = 0; i < IN_APP_PATTERNS.length; i++) {
      if (IN_APP_PATTERNS[i][1].test(UA)) return IN_APP_PATTERNS[i][0];
    }
    // Generic Android WebView carries a "; wv)" token.
    if (/Android/i.test(UA) && /;\s*wv\)/i.test(UA)) return 'android-webview';
    // iOS WKWebView that is neither Safari nor a known browser:
    // real Safari always reports both "Version/<n>" and "Safari/".
    if (/iPhone|iPad|iPod/i.test(UA) && !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo/i.test(UA)) {
      if (!/Version\/\d/i.test(UA) || !/Safari\//i.test(UA)) return 'ios-webview';
    }
    return '';
  }

  function detectBrowser() {
    if (/Edg\//i.test(UA) || /EdgiOS|EdgA/i.test(UA)) return 'edge';
    if (/OPR\//i.test(UA) || /OPiOS|Opera/i.test(UA)) return 'opera';
    if (/SamsungBrowser/i.test(UA)) return 'samsung';
    if (/UCBrowser/i.test(UA)) return 'uc';
    if (/DuckDuckGo/i.test(UA)) return 'duckduckgo';
    if (/Firefox\//i.test(UA) || /FxiOS/i.test(UA)) return 'firefox';
    if (/CriOS|Chrome\//i.test(UA)) return 'chrome';
    if (/Safari\//i.test(UA) && /Apple/i.test(VENDOR)) return 'safari';
    // Most in-app WebViews ship no browser token at all.
    if (/AppleWebKit/i.test(UA)) return 'webview';
    return 'unknown';
  }

  var env = {
    ua: UA,
    ios:
      /iPhone|iPad|iPod/i.test(UA) ||
      // iPadOS 13+ reports as desktop Mac but exposes touch points.
      (/Macintosh/i.test(UA) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1),
    android: /Android/i.test(UA),
    webkit: /AppleWebKit/i.test(UA) && !/Chrome|Chromium|Edg\//i.test(UA),
    browser: detectBrowser(),
    inApp: detectInApp(),
    iosVersion: 0,
    safariVersion: 0
  };

  var iosMatch = UA.match(/OS (\d+)[._](\d+)/);
  if (iosMatch && env.ios) env.iosVersion = parseFloat(iosMatch[1] + '.' + iosMatch[2]);
  var safariMatch = UA.match(/Version\/(\d+\.\d+)/);
  if (safariMatch && env.browser === 'safari') env.safariVersion = parseFloat(safariMatch[1]);

  env.isInApp = !!env.inApp;
  env.isMeta = /^(instagram|facebook|messenger|threads)$/.test(env.inApp);

  addClass('mm-browser-' + env.browser);
  if (env.ios) addClass('mm-ios');
  if (env.android) addClass('mm-android');
  if (env.isInApp) {
    addClass('mm-inapp');
    addClass('mm-inapp--' + env.inApp);
    window.__mmInAppBrowser = true;
  }

  /* =========================================================================
   * 2. Storage that never throws
   * -------------------------------------------------------------------------
   * Instagram/TikTok WebViews and Safari with "Block all cookies" make
   * localStorage access throw a SecurityError. One unguarded read used to kill
   * an entire IIFE -- which is why add-to-cart silently stopped binding in
   * those browsers. We swap in a memory-backed store so nothing throws.
   * =======================================================================*/

  function ownKeys(o) {
    var out = [];
    for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) out.push(k);
    return out;
  }

  function memoryStorage() {
    var data = {};
    return {
      get length() {
        return ownKeys(data).length;
      },
      key: function (i) {
        var k = ownKeys(data)[i];
        return k === undefined ? null : k;
      },
      getItem: function (k) {
        return Object.prototype.hasOwnProperty.call(data, String(k)) ? data[String(k)] : null;
      },
      setItem: function (k, v) {
        data[String(k)] = String(v);
      },
      removeItem: function (k) {
        delete data[String(k)];
      },
      clear: function () {
        data = {};
      },
      __mmMemory: true
    };
  }

  function storageWorks(kind) {
    try {
      var s = window[kind];
      if (!s) return false;
      s.setItem('__mm_probe__', '1');
      s.removeItem('__mm_probe__');
      return true;
    } catch (e) {
      return false;
    }
  }

  var storageState = { local: true, session: true };

  function installStorage(kind) {
    if (storageWorks(kind)) return;
    storageState[kind === 'localStorage' ? 'local' : 'session'] = false;
    var shim = memoryStorage();
    try {
      Object.defineProperty(window, kind, {
        configurable: true,
        get: function () {
          return shim;
        }
      });
    } catch (e) {
      try {
        window[kind] = shim;
      } catch (e2) {
        /* sealed: callers must go through MMCompat.storage */
      }
    }
  }

  installStorage('localStorage');
  installStorage('sessionStorage');

  if (!storageState.local || !storageState.session) addClass('mm-no-storage');

  /** Always-safe helpers, independent of whether the shim was installed. */
  var storage = {
    available: function (kind) {
      return kind === 'session' ? storageState.session : storageState.local;
    },
    get: function (key, fallback) {
      try {
        var v = window.localStorage.getItem(key);
        return v === null || v === undefined ? (fallback === undefined ? null : fallback) : v;
      } catch (e) {
        return fallback === undefined ? null : fallback;
      }
    },
    set: function (key, value) {
      try {
        window.localStorage.setItem(key, String(value));
        return true;
      } catch (e) {
        return false;
      }
    },
    remove: function (key) {
      try {
        window.localStorage.removeItem(key);
        return true;
      } catch (e) {
        return false;
      }
    },
    getJSON: function (key, fallback) {
      var raw = storage.get(key, null);
      if (raw === null) return fallback;
      try {
        return JSON.parse(raw);
      } catch (e) {
        return fallback;
      }
    },
    setJSON: function (key, value) {
      try {
        return storage.set(key, JSON.stringify(value));
      } catch (e) {
        return false;
      }
    },
    session: {
      get: function (key, fallback) {
        try {
          var v = window.sessionStorage.getItem(key);
          return v === null || v === undefined ? (fallback === undefined ? null : fallback) : v;
        } catch (e) {
          return fallback === undefined ? null : fallback;
        }
      },
      set: function (key, value) {
        try {
          window.sessionStorage.setItem(key, String(value));
          return true;
        } catch (e) {
          return false;
        }
      },
      remove: function (key) {
        try {
          window.sessionStorage.removeItem(key);
          return true;
        } catch (e) {
          return false;
        }
      }
    }
  };

  /** Are first-party cookies actually persisted? The cart depends on it. */
  function cookiesWork() {
    try {
      if (navigator.cookieEnabled === false) return false;
      document.cookie = '__mm_ck__=1;path=/;SameSite=Lax';
      var ok = document.cookie.indexOf('__mm_ck__=') !== -1;
      document.cookie = '__mm_ck__=;path=/;max-age=0;SameSite=Lax';
      return ok;
    } catch (e) {
      return false;
    }
  }

  env.cookies = cookiesWork();
  if (!env.cookies) addClass('mm-no-cookies');

  /* =========================================================================
   * 3. Polyfills the theme assumes
   * =======================================================================*/

  if (window.Element && !Element.prototype.closest) {
    if (!Element.prototype.matches) {
      Element.prototype.matches = Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
    }
    Element.prototype.closest = function (sel) {
      var el = this;
      while (el && el.nodeType === 1) {
        if (el.matches(sel)) return el;
        el = el.parentElement || el.parentNode;
      }
      return null;
    };
  }

  /**
   * Element.closest() starting from an arbitrary event target.
   *
   * `event.target` is not always an Element: it is `document` for a
   * document-level mouseenter/scroll, `window` for resize, and a text node in
   * some engines. Calling target.closest() directly then throws a TypeError
   * that aborts the whole handler — which is how delegated add-to-cart and
   * prefetch listeners died on some browsers but not others.
   */
  function closestFrom(target, selector) {
    var node = target;
    // Hop off non-Element targets (text nodes, comments) to their parent.
    while (node && node.nodeType !== 1) {
      node = node.parentElement || node.parentNode || null;
      if (node === document || node === window) return null;
    }
    if (!node || typeof node.closest !== 'function') return null;
    try {
      return node.closest(selector);
    } catch (e) {
      return null;
    }
  }

  if (window.NodeList && !NodeList.prototype.forEach) {
    NodeList.prototype.forEach = Array.prototype.forEach;
  }
  if (window.DOMTokenList && !DOMTokenList.prototype.forEach) {
    DOMTokenList.prototype.forEach = Array.prototype.forEach;
  }

  // Safari only shipped requestIdleCallback in 17.4.
  if (!window.requestIdleCallback) {
    window.requestIdleCallback = function (cb, opts) {
      var start = Date.now();
      return window.setTimeout(function () {
        cb({
          didTimeout: false,
          timeRemaining: function () {
            return Math.max(0, 50 - (Date.now() - start));
          }
        });
      }, (opts && opts.timeout) || 1);
    };
    window.cancelIdleCallback = function (id) {
      window.clearTimeout(id);
    };
  }

  if (window.Promise && !Promise.allSettled) {
    Promise.allSettled = function (list) {
      return Promise.all(
        Array.prototype.map.call(list, function (p) {
          return Promise.resolve(p).then(
            function (value) {
              return { status: 'fulfilled', value: value };
            },
            function (reason) {
              return { status: 'rejected', reason: reason };
            }
          );
        })
      );
    };
  }

  if (window.String && !String.prototype.replaceAll) {
    String.prototype.replaceAll = function (search, replacement) {
      if (Object.prototype.toString.call(search) === '[object RegExp]') {
        return this.replace(search, replacement);
      }
      return this.split(search).join(replacement);
    };
  }

  if (!window.structuredClone) {
    window.structuredClone = function (value) {
      return JSON.parse(JSON.stringify(value));
    };
  }

  // navigator.clipboard is missing over http and inside several WebViews.
  function legacyCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      if (ta.setSelectionRange) ta.setSelectionRange(0, ta.value.length);
      var ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) {
      return false;
    }
  }

  function copyText(text) {
    var str = String(text == null ? '' : text);
    if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
      return navigator.clipboard.writeText(str).catch(function () {
        if (legacyCopy(str)) return;
        throw new Error('copy failed');
      });
    }
    return legacyCopy(str) ? Promise.resolve() : Promise.reject(new Error('copy failed'));
  }

  /* =========================================================================
   * 4. CSS feature detection -> classes for mm-compat.css
   * =======================================================================*/

  function supports(prop, value) {
    try {
      return !!(window.CSS && CSS.supports && CSS.supports(prop, value));
    } catch (e) {
      return false;
    }
  }

  function supportsSelector(sel) {
    try {
      return !!(window.CSS && CSS.supports && CSS.supports('selector(' + sel + ')'));
    } catch (e) {
      return false;
    }
  }

  var features = {
    has: supportsSelector(':has(*)'),
    clip: supports('overflow', 'clip'),
    dvh: supports('height', '100dvh'),
    backdrop: supports('backdrop-filter', 'blur(2px)') || supports('-webkit-backdrop-filter', 'blur(2px)'),
    colorMix: supports('color', 'color-mix(in srgb, red 50%, blue)'),
    container: supports('container-type', 'inline-size'),
    aspectRatio: supports('aspect-ratio', '1/1'),
    inert: 'inert' in document.createElement('div'),
    dialog: typeof window.HTMLDialogElement === 'function',
    popover: 'popover' in document.createElement('div'),
    importmap: (function () {
      try {
        return !!(window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('importmap'));
      } catch (e) {
        return false;
      }
    })(),
    viewTransition: typeof document.startViewTransition === 'function',
    intersectionObserver: typeof window.IntersectionObserver === 'function',
    resizeObserver: typeof window.ResizeObserver === 'function',
    abort: typeof window.AbortController === 'function'
  };

  if (!features.has) addClass('mm-no-has');
  if (!features.clip) addClass('mm-no-clip');
  if (!features.dvh) addClass('mm-no-dvh');
  if (!features.backdrop) addClass('mm-no-backdrop');
  if (!features.colorMix) addClass('mm-no-color-mix');
  if (!features.container) addClass('mm-no-container');
  if (!features.importmap) addClass('mm-no-importmap');

  // Modules + import maps are required by the Horizon base theme. Without them
  // every @theme/* import fails and the native add-to-cart never binds, so the
  // MM fallback path has to take over.
  env.needsLegacyCart = !features.importmap;
  if (env.needsLegacyCart) addClass('mm-legacy-cart');

  /* =========================================================================
   * 5. Hardened cart transport + single cart-mutation bus
   * =======================================================================*/

  function cartRoot() {
    var r = '/';
    if (window.Shopify && window.Shopify.routes && typeof window.Shopify.routes.root === 'string') {
      r = window.Shopify.routes.root;
    } else if (window.MM_LOCALE_CONFIG && window.MM_LOCALE_CONFIG.roots) {
      var cur = window.MM_LOCALE_CONFIG.current;
      if (window.MM_LOCALE_CONFIG.roots[cur]) r = window.MM_LOCALE_CONFIG.roots[cur];
    }
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function cartUrl(path) {
    return cartRoot() + String(path || '').replace(/^\//, '');
  }

  var CART_TIMEOUT = 15000;
  var RETRY_STATUS = { 408: 1, 425: 1, 429: 1, 500: 1, 502: 1, 503: 1, 504: 1 };

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /**
   * A cart request that behaves the same in every engine:
   *  - explicit same-origin credentials (in-app WebViews do not always default to it)
   *  - hard timeout via AbortController, degrading to a plain timer
   *  - tolerant parsing: an HTML error page never becomes an unhandled exception
   *  - retries on transient statuses and on network resets
   */
  function cartRequest(path, options, attempt) {
    options = options || {};
    attempt = attempt || 0;

    var headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    if (options.headers) {
      for (var k in options.headers) {
        if (Object.prototype.hasOwnProperty.call(options.headers, k)) headers[k] = options.headers[k];
      }
    }

    var init = {
      method: options.method || 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: headers
    };
    if (options.body !== undefined && options.body !== null) init.body = options.body;

    var controller = features.abort ? new AbortController() : null;
    if (controller) init.signal = controller.signal;

    var timedOut = false;
    var timer = setTimeout(function () {
      timedOut = true;
      if (controller) {
        try {
          controller.abort();
        } catch (e) {}
      }
    }, options.timeout || CART_TIMEOUT);

    return fetch(cartUrl(path), init)
      .then(function (res) {
        clearTimeout(timer);
        return res.text().then(function (text) {
          var data = null;
          if (text) {
            try {
              data = JSON.parse(text);
            } catch (e) {
              data = null;
            }
          }

          if (res.ok && data && typeof data === 'object') return data;

          if (RETRY_STATUS[res.status] && attempt < 3) {
            return delay(400 * Math.pow(2, attempt)).then(function () {
              return cartRequest(path, options, attempt + 1);
            });
          }

          if (data && typeof data === 'object') {
            var apiErr = new Error(data.description || data.message || 'Cart API error');
            apiErr.status = res.status;
            apiErr.data = data;
            throw apiErr;
          }

          var err = new Error('Cart API returned a non-JSON response (' + res.status + ')');
          err.status = res.status;
          err.body = String(text || '').slice(0, 200);
          throw err;
        });
      })
      .catch(function (err) {
        clearTimeout(timer);
        if (timedOut) {
          if (attempt < 2) {
            return delay(300).then(function () {
              return cartRequest(path, options, attempt + 1);
            });
          }
          var t = new Error('Cart request timed out');
          t.timeout = true;
          throw t;
        }
        // Network-level failure: dropped WebView connection, offline, TLS reset.
        if (err && err.name === 'TypeError' && attempt < 2) {
          return delay(400 * (attempt + 1)).then(function () {
            return cartRequest(path, options, attempt + 1);
          });
        }
        throw err;
      });
  }

  function getCart() {
    return cartRequest('cart.js');
  }

  /**
   * Add to cart, then confirm against /cart.js.
   * The confirmation matters: when cookies are partitioned or blocked the POST
   * can return 200 while the cart cookie is dropped, so the item silently
   * disappears. We surface that as a real error instead of a fake success.
   */
  function addToCart(id, quantity, properties) {
    var variantId = parseInt(String(id == null ? '' : id).replace(/\D/g, ''), 10);
    if (!variantId) return Promise.reject(new Error('invalid variant id'));

    var payload = { id: variantId, quantity: parseInt(quantity, 10) || 1 };
    if (properties) payload.properties = properties;

    return cartRequest('cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (item) {
      return getCart().then(
        function (cart) {
          if (!cart || cart.item_count === 0) {
            var err = new Error('cart-not-persisted');
            err.code = 'CART_NOT_PERSISTED';
            err.cookies = env.cookies;
            throw err;
          }
          emit('mm:cart-changed', { cart: cart, item: item, source: 'MMCompat.addToCart' });
          return { item: item, cart: cart };
        },
        function () {
          emit('mm:cart-changed', { item: item, source: 'MMCompat.addToCart' });
          return { item: item, cart: null };
        }
      );
    });
  }

  /* --- one bus instead of several competing window.fetch patches --- */

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var listeners = [];

  function emit(name, detail) {
    var ev;
    try {
      ev = new CustomEvent(name, { detail: detail, bubbles: true });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, true, false, detail);
    }
    document.dispatchEvent(ev);
  }

  function notifyMutation(info) {
    for (var i = 0; i < listeners.length; i++) {
      try {
        listeners[i](info);
      } catch (e) {
        report(e, 'cart-mutation-listener');
      }
    }
    emit('mm:cart-mutation', info);
  }

  function installCartBus() {
    if (window.__mmCartBusInstalled) return;
    window.__mmCartBusInstalled = true;

    if (typeof window.fetch === 'function') {
      var nativeFetch = window.fetch;

      // Several independent scripts read /cart.js at the same moment on page
      // load (5 parallel requests were measured). Concurrent plain GETs of the
      // same URL now share one network request; every caller gets its own
      // clone, so nobody consumes another caller's body. Kept per URL because
      // /ar/cart.js returns Arabic titles. Dropped as soon as a cart mutation
      // starts, so reads after an add/change never see a pre-mutation cart.
      var CART_READ_RE = /^(?:https?:\/\/[^/]+)?(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/cart\.js(?:\?|$)/i;
      var inflightCartReads = {};

      window.fetch = function (input, init) {
        var url = '';
        try {
          url = typeof input === 'string' ? input : input && input.url ? input.url : String(input);
        } catch (e) {
          url = '';
        }
        var isCart = CART_MUTATION_RE.test(url);

        if (isCart) inflightCartReads = {};

        var method = String((init && init.method) || 'GET').toUpperCase();
        if (
          typeof input === 'string' &&
          method === 'GET' &&
          !(init && (init.signal || init.body)) &&
          CART_READ_RE.test(url)
        ) {
          var shared = inflightCartReads[url];
          if (!shared) {
            shared = nativeFetch.apply(this, arguments);
            inflightCartReads[url] = shared;
            var clear = function () {
              if (inflightCartReads[url] === shared) delete inflightCartReads[url];
            };
            shared.then(clear, clear);
          }
          return shared.then(function (res) {
            return res.clone();
          });
        }

        var result = nativeFetch.apply(this, arguments);
        if (!isCart || !result || typeof result.then !== 'function') return result;

        return result.then(function (res) {
          if (res && res.ok) {
            try {
              notifyMutation({ url: url, transport: 'fetch', status: res.status });
            } catch (e) {
              report(e, 'cart-bus-fetch');
            }
          }
          return res;
        });
      };
      window.fetch.__mmNative = nativeFetch;
    }

    if (window.XMLHttpRequest && XMLHttpRequest.prototype) {
      var proto = XMLHttpRequest.prototype;
      var nativeOpen = proto.open;
      var nativeSend = proto.send;
      proto.open = function (method, url) {
        try {
          this.__mmUrl = url || '';
          this.__mmMethod = String(method || '').toUpperCase();
        } catch (e) {}
        return nativeOpen.apply(this, arguments);
      };
      proto.send = function () {
        var xhr = this;
        try {
          if (xhr.__mmMethod === 'POST' && CART_MUTATION_RE.test(xhr.__mmUrl || '')) {
            xhr.addEventListener('load', function () {
              if (xhr.status >= 200 && xhr.status < 300) {
                notifyMutation({ url: xhr.__mmUrl, transport: 'xhr', status: xhr.status });
              }
            });
          }
        } catch (e) {}
        return nativeSend.apply(this, arguments);
      };
    }
  }

  installCartBus();

  /* --- bfcache / tab restore: in-app browsers restore aggressively --- */
  window.addEventListener('pageshow', function (e) {
    if (e && e.persisted) {
      emit('mm:page-restored', {});
      getCart()
        .then(function (cart) {
          emit('mm:cart-changed', { cart: cart, source: 'bfcache' });
        })
        .catch(function () {});
    }
  });

  /* =========================================================================
   * 6. Error capture
   * =======================================================================*/

  var WEBVIEW_NOISE =
    /java object is gone|navigation_performance_logger|enableButtonsClickedMetaDataLogging|enableDidUserTypeOnKeyboardLogging|__gCrWeb|ResizeObserver loop/i;

  var errors = [];
  var DEBUG = false;
  try {
    DEBUG = /[?&]mm_debug=1(?:&|$)/.test(window.location.search) || storage.get('mm_debug') === '1';
  } catch (e) {}

  function report(error, context) {
    var message = (error && error.message) || String(error || '');
    if (WEBVIEW_NOISE.test(message)) return;
    var entry = {
      message: message,
      context: context || '',
      stack: (error && error.stack) || '',
      at: Date.now()
    };
    errors.push(entry);
    if (errors.length > 50) errors.shift();
    emit('mm:error', entry);
    if (DEBUG && window.console && console.warn) {
      console.warn('[MMCompat] ' + (context || '') + ':', message, error);
    }
  }

  window.addEventListener(
    'error',
    function (event) {
      var msg = event && (event.message || (event.error && event.error.message));
      if (WEBVIEW_NOISE.test(String(msg || ''))) {
        event.preventDefault();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        return true;
      }
      // A cross-origin script reports an opaque "Script error." with no stack.
      // There is nothing actionable in it, so do not fill the log with them.
      if (!event.error && (!msg || /^script error/i.test(String(msg)))) return;
      report(event.error || new Error(String(msg)), 'window.error');
    },
    true
  );

  window.addEventListener('unhandledrejection', function (event) {
    var reason = event && event.reason;
    var msg = (reason && reason.message) || String(reason || '');
    if (WEBVIEW_NOISE.test(msg)) {
      event.preventDefault();
      return;
    }
    report(reason instanceof Error ? reason : new Error(msg), 'unhandledrejection');
  });

  /** Wrap a function so a browser-specific throw cannot kill the caller. */
  function safe(fn, context) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (e) {
        report(e, context || 'safe');
        return undefined;
      }
    };
  }

  function diagnostics() {
    return {
      version: VERSION,
      env: env,
      features: features,
      storage: { local: storageState.local, session: storageState.session },
      cookies: env.cookies,
      errors: errors.slice()
    };
  }

  /* =========================================================================
   * 7. Public API
   * =======================================================================*/

  window.MMCompat = {
    version: VERSION,
    env: env,
    features: features,
    storage: storage,
    supports: supports,
    supportsSelector: supportsSelector,
    closestFrom: closestFrom,
    copyText: copyText,
    cartUrl: cartUrl,
    cartRequest: cartRequest,
    getCart: getCart,
    addToCart: addToCart,
    onCartMutation: function (fn) {
      if (typeof fn === 'function') listeners.push(fn);
      return function () {
        var i = listeners.indexOf(fn);
        if (i !== -1) listeners.splice(i, 1);
      };
    },
    emit: emit,
    report: report,
    safe: safe,
    diagnostics: diagnostics
  };

  if (DEBUG && window.console && console.info) {
    console.info('[MMCompat]', diagnostics());
  }
})(window, document);
